import { Metadata } from 'next';

export interface SeoConfig {
  siteName: string;
  siteUrl: string;
  isStaging: boolean;
}

const LOCALES = ['fr-fr', 'en-us', 'es-419', 'pt-br', 'de-de', 'ja-jp'] as const;

export const createMetadataGenerator = (config: SeoConfig) => {
  const { siteName, siteUrl, isStaging } = config;

  const generateScopedMetadata = (params: {
    scope: string;
    title: string;
    description: string;
    path: string;
    keywords?: string[];
    imageUrl?: string;
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
  }): Metadata => {
    const {
      scope,
      title,
      description,
      path,
      keywords = [],
      imageUrl,
      author,
      publishedTime,
      modifiedTime
    } = params;

    const fullTitle = `${title} | ${siteName}`;
    const url = `${siteUrl}${path}`;
    const defaultImage = `${siteUrl}/images/og-default.jpg`;
    const image = imageUrl || defaultImage;
    const pathSuffix = path.split('/').slice(2).join('/');
    const hreflangBase = (l: string) => `${siteUrl}/${l}${pathSuffix ? `/${pathSuffix}` : ''}`;

    const metadata: Metadata = {
      title: fullTitle,
      description,
      keywords: keywords.join(', '),
      authors: author ? [{ name: author }] : undefined,
      openGraph: {
        type: scope === 'article' || scope === 'topic' ? 'article' : 'website',
        url,
        title: fullTitle,
        description,
        siteName,
        images: [
          {
            url: image,
            width: 1200,
            height: 630,
            alt: title,
          },
        ],
        ...(publishedTime && { publishedTime }),
        ...(modifiedTime && { modifiedTime }),
      },
      twitter: {
        card: 'summary_large_image',
        title: fullTitle,
        description,
        images: [image],
      },
      alternates: {
        canonical: url,
        languages: Object.fromEntries([
          ...LOCALES.map(l => [l, hreflangBase(l)]),
          ['x-default', hreflangBase('en-us')],
        ]),
      },
      robots: {
        index: !isStaging,
        follow: !isStaging,
      },
    };

    return metadata;
  };

  return {
    generateScopedMetadata,
    isStaging
  };
};
